import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { COMPANY } from "../data/site";

/**
 * Lightweight SEO helper that updates document.title and meta tags
 * without depending on react-helmet.
 */
function upsertMeta(attr, name, content) {
    if (!content) return;
    let el = document.head.querySelector(`meta[${attr}="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}

function upsertLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function upsertJsonLd(id, json) {
    let el = document.getElementById(id);
    if (!el) {
        el = document.createElement("script");
        el.id = id;
        el.type = "application/ld+json";
        document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(json);
}

export default function SEO({ title, description, keywords, image }) {
    const location = useLocation();
    useEffect(() => {
        const fullTitle = title ? `${title} | ${COMPANY.name}` : `${COMPANY.name} | ${COMPANY.shortPitch}`;
        document.title = fullTitle;
        upsertMeta("name", "description", description || COMPANY.shortPitch);
        if (keywords && keywords.length) {
            upsertMeta("name", "keywords", keywords.join(", "));
        }
        upsertMeta("property", "og:title", fullTitle);
        upsertMeta("property", "og:description", description || COMPANY.shortPitch);
        upsertMeta("property", "og:type", "website");
        upsertMeta("property", "og:image", image || COMPANY.logo);
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertLink("canonical", `${COMPANY.website}${location.pathname}`);

        // LocalBusiness JSON-LD (global, refreshed on each page)
        upsertJsonLd("ld-localbusiness", {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: COMPANY.name,
            image: COMPANY.logo,
            url: COMPANY.website,
            email: COMPANY.email,
            telephone: COMPANY.phones[0].value,
            address: {
                "@type": "PostalAddress",
                streetAddress: COMPANY.address.street,
                postalCode: COMPANY.address.postalCode,
                addressLocality: COMPANY.address.city,
                addressCountry: COMPANY.address.country,
            },
            areaServed: COMPANY.areas,
            description: COMPANY.shortPitch,
        });
    }, [title, description, keywords, image, location.pathname]);
    return null;
}
